import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { qrApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { CheckCircle, XCircle, CircleNotch, Clock } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

export default function ReprintRequestsPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const loadRequests = useCallback(async () => {
    try {
      const res = await qrApi.listReprintRequests();
      setRequests(res.data);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleReview = async (approved) => {
    setSubmitting(true);
    try {
      await qrApi.reviewReprintRequest(selectedReq.id, approved, adminNotes);
      toast.success(approved ? 'Reprint approved' : 'Reprint rejected');
      setReviewOpen(false);
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to review');
    } finally { setSubmitting(false); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { icon: Clock, cls: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
      approved: { icon: CheckCircle, cls: 'bg-green-50 text-green-600 border-green-200' },
      rejected: { icon: XCircle, cls: 'bg-red-50 text-red-600 border-red-200' },
    };
    const s = map[status] || map.pending;
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${s.cls}`}>
        <Icon className="w-3 h-3" /> {status}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reprint-requests-page">
        <PageHeader
          title={t('reprint.title')}
          description={t('reprint.subtitle')}
        />

        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-zinc-800 hover:bg-transparent' : 'border-zinc-200 hover:bg-transparent'}>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>QR ID</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{t('reprint.requestedBy')}</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Reason</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Date</TableHead>
                  <TableHead className={`${isDark ? 'text-zinc-400' : 'text-zinc-500'} text-right`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8"><CircleNotch className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
                ) : requests.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className={`text-center py-8 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('reprint.noRequests')}</TableCell></TableRow>
                ) : requests.map((req) => (
                  <TableRow key={req.id} className={isDark ? 'border-zinc-800 hover:bg-zinc-800/50' : 'border-zinc-200 hover:bg-zinc-50'}
                    data-testid={`reprint-row-${req.id}`}>
                    <TableCell className="font-mono text-xs">{req.qr_id?.substring(0, 12)}...</TableCell>
                    <TableCell className={isDark ? 'text-zinc-100' : 'text-zinc-900'}>{req.requested_by_name}</TableCell>
                    <TableCell className={`max-w-[200px] truncate ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>{req.reason}</TableCell>
                    <TableCell>{statusBadge(req.status)}</TableCell>
                    <TableCell className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{formatDateTime(req.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {req.status === 'pending' ? (
                        <Button size="sm" onClick={() => { setSelectedReq(req); setAdminNotes(''); setReviewOpen(true); }}
                          className="bg-brand-600 hover:bg-brand-700 text-white text-xs"
                          data-testid={`review-btn-${req.id}`}>
                          {t('common.review')}
                        </Button>
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                          {req.reviewed_by_name && `by ${req.reviewed_by_name}`}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Review Dialog */}
        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{t('reprint.reviewTitle')}</DialogTitle>
            </DialogHeader>
            {selectedReq && (
              <div className="space-y-4">
                <div className={`p-3 rounded-md border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>QR ID</p>
                  <p className="font-mono text-xs">{selectedReq.qr_id}</p>
                  <p className={`text-xs mt-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Requested By</p>
                  <p className="text-sm">{selectedReq.requested_by_name}</p>
                  <p className={`text-xs mt-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Reason</p>
                  <p className="text-sm">{selectedReq.reason}</p>
                </div>
                <div className="space-y-2">
                  <label className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{t('common.adminNotes')}</label>
                  <Input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Optional notes..."
                    className={isDark ? 'bg-zinc-800 border-zinc-700' : ''} />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleReview(false)} disabled={submitting}
                className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-reprint-btn">
                <XCircle className="w-4 h-4 mr-1" /> {t('common.reject')}
              </Button>
              <Button onClick={() => handleReview(true)} disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-reprint-btn">
                {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> {t('common.approve')}</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
